// @ts-nocheck - TypeScript has issues with react-hook-form generic types in this version
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useFieldArray } from 'react-hook-form'
import { useMemo, useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Info, MessageSquare, Mail, Plus, Trash2, CheckCheck, ExternalLink } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Switch } from '@/shared/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Separator } from '@/shared/components/ui/separator'
import { useToast } from '@/shared/hooks/use-toast'
import { ImageUpload } from '@/shared/components/ui/image-upload'
import { uploadFile } from '@/shared/services/uploads'
import { campaniaCreateSchema, type CampaniaCreateInput } from '../schemas/campania-create'
import { useCreateCampania, useUpdateCampania, useFiltersData } from '@/modules/multichannel/hooks/useCampanias'
import { useReporteWhatsAppPorCampania, useCreateReporteWhatsApp } from '@/modules/cronograma/hooks/useReportes'
import type { CampaniaDetalle } from '@/modules/multichannel/types/campania'

interface CampaniaCreateFormProps {
  initialData?: CampaniaDetalle
  mode?: 'create' | 'edit' | 'duplicate'
}

export function CampaniaCreateForm({ initialData, mode = 'create' }: CampaniaCreateFormProps) {
  const isEdit = mode === 'edit'
  const isDuplicate = mode === 'duplicate'
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: filtersData } = useFiltersData()
  const createCampania = useCreateCampania()
  const updateCampania = useUpdateCampania()
  const createReporteWA = useCreateReporteWhatsApp()

  // Cargar reporte WA existente (solo en modo edición)
  const { data: reporteWA } = useReporteWhatsAppPorCampania(
    isEdit && initialData ? initialData.id : null,
    isEdit && !!initialData
  )

  // Estados WhatsApp content
  const [waMensaje, setWaMensaje] = useState('')
  const [waPendingFile, setWaPendingFile] = useState<File | null>(null)
  const [waImageUrl, setWaImageUrl] = useState<string | null>(null)
  const [waIsUploading, setWaIsUploading] = useState(false)
  const [waBotones, setWaBotones] = useState<{ texto: string; url: string }[]>([])
  const [waFooter, setWaFooter] = useState('')
  const [showInfoWA, setShowInfoWA] = useState(false)

  // Pre-cargar contenido WA del reporte existente
  useEffect(() => {
    if (reporteWA) {
      setWaMensaje(reporteWA.mensaje_texto || '')
      setWaImageUrl(reporteWA.imagen_url || null)
      setWaBotones(reporteWA.botones_cta || [])
      setWaFooter(reporteWA.footer || '')
    }
  }, [reporteWA])

  const handleWaImageChange = useCallback((file: File | null, previewUrl: string | null) => {
    setWaPendingFile(file)
    if (file) {
      setWaImageUrl(previewUrl)
    } else {
      setWaImageUrl(null)
    }
  }, [])

  // Estados para mostrar descripciones
  const [showInfoAsesor, setShowInfoAsesor] = useState(false)
  const [showInfoMarca, setShowInfoMarca] = useState(false)
  const [showInfoConfig, setShowInfoConfig] = useState(false)
  const [showInfoTipos, setShowInfoTipos] = useState(false)
  const [showInfoEstados, setShowInfoEstados] = useState(false)
  const [showInfoContenido, setShowInfoContenido] = useState(false)
  const [showInfoProgramacion, setShowInfoProgramacion] = useState(false)
  const [showInfoAdicional, setShowInfoAdicional] = useState(false)

  const form = useForm<CampaniaCreateInput>({
    resolver: zodResolver(campaniaCreateSchema),
    shouldFocusError: false,
    defaultValues: initialData ? {
      nombre_campania: initialData.nombre_campania,
      remitente: initialData.remitente,
      target: initialData.target,
      base_segmentada: initialData.base_segmentada,
      marca: initialData.marca,
      marca_secundaria: initialData.marca_secundaria ?? undefined,
      tipo: initialData.tipo,
      formato: initialData.formato,
      estado_contenido: initialData.estado_contenido,
      estado_envio: initialData.estado_envio,
      estado_reporte: initialData.estado_reporte,
      estado_aprobacion: initialData.estado_aprobacion ?? undefined,
      tipo_envio: initialData.tipo_envio,
      nombre: initialData.nombre,
      requiere_aprobacion: initialData.requiere_aprobacion,
      asunto: initialData.asunto ?? undefined,
      pre_encabezado: initialData.pre_encabezado ?? undefined,
      titulo_envio: initialData.titulo_envio ?? undefined,
      archivo_url: initialData.archivo_url ?? undefined,
      contenido_html: initialData.contenido_html ?? undefined,
      fecha_publicacion: initialData.fecha_publicacion ?? undefined,
      hora: initialData.hora ?? undefined,
      reporte: initialData.reporte ?? undefined,
      notas: initialData.notas ?? undefined,
      configuracion_fk_id: initialData.configuracion_fk_id ?? undefined,
      marca_principal_fk_id: initialData.marca_principal_fk_id ?? undefined,
      marca_secundaria_fk_id: initialData.marca_secundaria_fk_id ?? undefined,
      tipo_fk_id: initialData.tipo_fk_id ?? undefined,
      formato_fk_id: initialData.formato_fk_id ?? undefined,
      estado_contenido_fk_id: initialData.estado_contenido_fk_id ?? undefined,
      estado_envio_fk_id: initialData.estado_envio_fk_id ?? undefined,
      estado_reporte_fk_id: initialData.estado_reporte_fk_id ?? undefined,
      estado_aprobacion_fk_id: initialData.estado_aprobacion_fk_id ?? undefined,
      tipo_envio_fk_id: initialData.tipo_envio_fk_id ?? undefined,
      asesor_tonal_id: initialData.asesor_tonal_id ?? undefined,
    } : {
      nombre_campania: '',
      remitente: '',
      target: '',
      base_segmentada: '',
      nombre: '',
      fecha_publicacion: '',
      hora: '',
      requiere_aprobacion: false,
      estado_contenido_fk_id: 1,  // En proceso
      estado_envio_fk_id: 3,      // Pendiente
      estado_reporte_fk_id: 1,    // Pendiente
    },
  })

  const selectedLabId = form.watch('marca_principal_fk_id')
  const selectedMarcaSecId = form.watch('marca_secundaria_fk_id')
  const selectedConfigId = form.watch('configuracion_fk_id')
  const requiereAprobacion = form.watch('requiere_aprobacion')
  const selectedTipoId = form.watch('tipo_fk_id')
  const selectedTipoEnvioId = form.watch('tipo_envio_fk_id')

  // Derivar nombre del tipo de campaña para mostrar/ocultar secciones
  const tipoNombre = useMemo(() => {
    if (!filtersData?.tipos_campania || !selectedTipoId) return ''
    return (filtersData.tipos_campania.find(t => t.id === selectedTipoId)?.nombre || '').toLowerCase()
  }, [filtersData?.tipos_campania, selectedTipoId])

  // Derivar nombre del tipo de envío
  const tipoEnvioNombre = useMemo(() => {
    if (!filtersData?.tipos_envio || !selectedTipoEnvioId) return ''
    return (filtersData.tipos_envio.find(t => t.id === selectedTipoEnvioId)?.nombre || '').toLowerCase()
  }, [filtersData?.tipos_envio, selectedTipoEnvioId])

  const showEmail = tipoNombre && (tipoNombre.includes('email') || tipoNombre.includes('mail'))
  const showWhatsApp = tipoNombre && tipoNombre.includes('whatsapp')

  // Filtrar marcas secundarias por laboratorio seleccionado
  const filteredMarcasSecundarias = useMemo(() => {
    if (!filtersData?.marcas_secundarias || !selectedLabId) return []
    return filtersData.marcas_secundarias.filter(
      (m) => m.marca_principal_id === selectedLabId
    )
  }, [filtersData?.marcas_secundarias, selectedLabId])

  // Filtrar configuraciones por marca secundaria seleccionada
  const filteredConfiguraciones = useMemo(() => {
    if (!filtersData?.configuraciones_campania || !selectedMarcaSecId) return []
    return filtersData.configuraciones_campania.filter(
      (c) => c.marca_secundaria_id === selectedMarcaSecId
    )
  }, [filtersData?.configuraciones_campania, selectedMarcaSecId])

  // Obtener la configuración seleccionada
  const selectedConfig = useMemo(() => {
    if (!filtersData?.configuraciones_campania || !selectedConfigId) return null
    return filtersData.configuraciones_campania.find((c) => c.id === selectedConfigId) || null
  }, [filtersData?.configuraciones_campania, selectedConfigId])

  // Handler para cambio de laboratorio - limpiar cascada
  const handleLabChange = (value: string) => {
    const labId = parseInt(value)
    form.setValue('marca_principal_fk_id', labId)
    form.setValue('marca_secundaria_fk_id', undefined)
    form.setValue('configuracion_fk_id', undefined)
    // Limpiar campos auto-rellenados
    form.setValue('nombre', undefined)
    form.setValue('remitente', '')
    form.setValue('target', '')
    form.setValue('base_segmentada', '')
    form.setValue('nombre_campania', '')
  }

  // Handler para cambio de marca secundaria - limpiar config
  const handleMarcaSecChange = (value: string) => {
    const marcaId = parseInt(value)
    form.setValue('marca_secundaria_fk_id', marcaId)
    form.setValue('configuracion_fk_id', undefined)
    // Limpiar campos auto-rellenados
    form.setValue('nombre', undefined)
    form.setValue('remitente', '')
    form.setValue('target', '')
    form.setValue('base_segmentada', '')
    form.setValue('nombre_campania', '')
  }

  // Handler para selección de configuración - auto-rellenar campos
  const handleConfigChange = (value: string) => {
    const configId = parseInt(value)
    form.setValue('configuracion_fk_id', configId)
    const config = filtersData?.configuraciones_campania.find((c) => c.id === configId)
    if (config) {
      form.setValue('nombre_campania', config.nombre_campania)
      form.setValue('nombre', config.nombre_encargado)
      form.setValue('remitente', config.remitente)
      form.setValue('target', config.target)
      form.setValue('base_segmentada', config.base_segmentada)
    }
  }

  const onSubmit = async (data: CampaniaCreateInput) => {
    try {
      // Derivar CharFields de los FKs seleccionados
      const submitData = { ...data }

      // marca = nombre del laboratorio seleccionado
      const selectedLab = filtersData?.marcas_principales.find(
        (m) => m.id === data.marca_principal_fk_id
      )
      if (selectedLab) submitData.marca = selectedLab.nombre

      // marca_secundaria = nombre de la marca secundaria
      const selectedMarcaSec = filtersData?.marcas_secundarias.find(
        (m) => m.id === data.marca_secundaria_fk_id
      )
      if (selectedMarcaSec) submitData.marca_secundaria = selectedMarcaSec.nombre

      // Derivar tipo + tipo_fk_id
      const selectedTipo = filtersData?.tipos_campania.find(
        (t) => t.id === data.tipo_fk_id
      )
      if (selectedTipo) {
        submitData.tipo = selectedTipo.nombre
      }

      // Derivar formato + formato_fk_id
      const selectedFormato = filtersData?.formatos_campania.find(
        (f) => f.id === data.formato_fk_id
      )
      if (selectedFormato) {
        submitData.formato = selectedFormato.nombre
      }

      // Derivar tipo_envio + tipo_envio_fk_id
      const selectedTipoEnvio = filtersData?.tipos_envio.find(
        (t) => t.id === data.tipo_envio_fk_id
      )
      if (selectedTipoEnvio) {
        submitData.tipo_envio = selectedTipoEnvio.nombre
      }

      // Derivar estado_contenido + estado_contenido_fk_id
      const selectedEstContenido = filtersData?.estados_contenido.find(
        (e) => e.id === data.estado_contenido_fk_id
      )
      if (selectedEstContenido) {
        submitData.estado_contenido = selectedEstContenido.nombre
      }

      // Derivar estado_envio + estado_envio_fk_id
      const selectedEstEnvio = filtersData?.estados_envio.find(
        (e) => e.id === data.estado_envio_fk_id
      )
      if (selectedEstEnvio) {
        submitData.estado_envio = selectedEstEnvio.nombre
      }

      // Derivar estado_reporte + estado_reporte_fk_id
      const selectedEstReporte = filtersData?.estados_reporte.find(
        (e) => e.id === data.estado_reporte_fk_id
      )
      if (selectedEstReporte) {
        submitData.estado_reporte = selectedEstReporte.nombre
      }

      // Derivar estado_aprobacion + estado_aprobacion_fk_id
      if (data.estado_aprobacion_fk_id) {
        const selectedEstAprobacion = filtersData?.estados_aprobacion.find(
          (e) => e.id === data.estado_aprobacion_fk_id
        )
        if (selectedEstAprobacion) {
          submitData.estado_aprobacion = selectedEstAprobacion.nombre
        }
      }

      let campaniaId: number

      if (isEdit && initialData) {
        await updateCampania.mutateAsync({ id: initialData.id, data: submitData })
        campaniaId = initialData.id
      } else {
        const campania = await createCampania.mutateAsync(submitData)
        campaniaId = campania.id
      }

      // Guardar contenido WhatsApp si hay datos
      const tieneContenidoWA = waMensaje.trim() || waImageUrl || waPendingFile || waBotones.length > 0 || waFooter.trim()
      if (tieneContenidoWA) {
        let finalImageUrl = waImageUrl
        if (waPendingFile) {
          setWaIsUploading(true)
          try {
            const labName = filtersData?.marcas_principales.find(m => m.id === data.marca_principal_fk_id)?.nombre || ''
            const customName = `${labName}_${data.nombre_campania}`.replace(/\s+/g, '_')
            const uploadResponse = await uploadFile(waPendingFile, 'app/multichannel', customName)
            finalImageUrl = uploadResponse.url
          } catch {
            toast({ title: 'Error', description: 'No se pudo subir la imagen de WhatsApp', variant: 'destructive' })
            setWaIsUploading(false)
            return
          }
          setWaIsUploading(false)
        }

        await createReporteWA.mutateAsync({
          campania_id: campaniaId,
          titulo_campana: data.nombre_campania,
          base: reporteWA?.base || 0,
          entrega: reporteWA?.entrega || 0,
          leidos: reporteWA?.leidos || 0,
          clics: reporteWA?.clics || 0,
          unsub: reporteWA?.unsub || 0,
          mensaje_texto: waMensaje,
          imagen_url: finalImageUrl || undefined,
          botones_cta: waBotones.length > 0 ? waBotones : undefined,
          footer: waFooter || undefined,
          completado: reporteWA?.completado || false,
          habilitar_boton_excel: reporteWA?.habilitar_boton_excel || false,
        })
      }

      toast({
        title: isEdit ? 'Campaña actualizada' : 'Campaña creada',
        description: `La campaña "${submitData.nombre_campania}" ha sido ${isEdit ? 'actualizada' : 'creada exitosamente'}.`,
      })
      navigate(`/multichannel/campania/${campaniaId}`)
    } catch (error: any) {
      toast({
        title: isEdit ? 'Error al actualizar campaña' : 'Error al crear campaña',
        description: error.response?.data?.error || 'Ocurrió un error',
        variant: 'destructive',
      })
    }
  }

  const scrollToFirstError = useCallback(() => {
    setTimeout(() => {
      const firstInvalid = document.querySelector('[aria-invalid="true"]') as HTMLElement
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
        firstInvalid.focus()
      }
    }, 0)
  }, [])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, scrollToFirstError)} className="space-y-6">
        {/* Asesor Tonal */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle>Asesor Tonal</CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoAsesor(!showInfoAsesor)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoAsesor && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Asesor responsable de la campaña
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="asesor_tonal_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Asesor</FormLabel>
                  <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona un asesor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {filtersData?.asesores.map((asesor) => (
                        <SelectItem key={asesor.id} value={asesor.id.toString()}>
                          {asesor.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Información de Marca */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle>Información de Marca</CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoMarca(!showInfoMarca)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoMarca && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Selecciona laboratorio y marca
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="marca_principal_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Laboratorio *</FormLabel>
                      <FormMessage />
                    </div>
                    <Select onValueChange={handleLabChange} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un laboratorio" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filtersData?.marcas_principales.map((marca) => (
                          <SelectItem key={marca.id} value={marca.id.toString()}>
                            {marca.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="marca_secundaria_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Marca Secundaria *</FormLabel>
                      <FormMessage />
                    </div>
                    <Select
                      onValueChange={handleMarcaSecChange}
                      value={field.value?.toString()}
                      disabled={!selectedLabId}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={selectedLabId ? "Selecciona una marca" : "Selecciona laboratorio primero"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filteredMarcasSecundarias.map((marca) => (
                          <SelectItem key={marca.id} value={marca.id.toString()}>
                            {marca.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Configuración de Campaña */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle>Configuración de Campaña</CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoConfig(!showInfoConfig)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoConfig && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Selecciona una configuración para auto-rellenar campos
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="configuracion_fk_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Configuración</FormLabel>
                  <Select
                    onValueChange={handleConfigChange}
                    value={field.value?.toString()}
                    disabled={!selectedMarcaSecId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={selectedMarcaSecId ? "Selecciona una configuración" : "Selecciona marca secundaria primero"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {filteredConfiguraciones.map((config) => (
                        <SelectItem key={config.id} value={config.id.toString()}>
                          {config.nombre_campania}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nombre_campania"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Nombre de Campaña *</FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <Input
                      placeholder="Ej: Lanzamiento Producto Verano 2026"
                      readOnly={!isDuplicate && (!!selectedConfig || (isEdit && !!selectedConfigId))}
                      className={!isDuplicate && (selectedConfig || (isEdit && selectedConfigId)) ? 'bg-muted' : ''}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="nombre"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Encargado *</FormLabel>
                      <FormMessage />
                    </div>
                    <FormControl>
                      <Input
                        placeholder="Nombre del encargado"
                        readOnly={!!selectedConfig || (isEdit && !!selectedConfigId)}
                        className={selectedConfig || (isEdit && selectedConfigId) ? 'bg-muted' : ''}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="remitente"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Remitente *</FormLabel>
                      <FormMessage />
                    </div>
                    <FormControl>
                      <Input
                        placeholder="Nombre o email del remitente"
                        readOnly={!!selectedConfig || (isEdit && !!selectedConfigId)}
                        className={selectedConfig || (isEdit && selectedConfigId) ? 'bg-muted' : ''}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="target"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Target *</FormLabel>
                      <FormMessage />
                    </div>
                    <FormControl>
                      <Input
                        placeholder="Ej: Médicos generales"
                        readOnly={!!selectedConfig || (isEdit && !!selectedConfigId)}
                        className={selectedConfig || (isEdit && selectedConfigId) ? 'bg-muted' : ''}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="base_segmentada"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Base Segmentada *</FormLabel>
                      <FormMessage />
                    </div>
                    <FormControl>
                      <Input
                        placeholder="Ej: Base activa 2026"
                        readOnly={!!selectedConfig || (isEdit && !!selectedConfigId)}
                        className={selectedConfig || (isEdit && selectedConfigId) ? 'bg-muted' : ''}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Tipos y Formatos */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle>Tipos y Formatos</CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoTipos(!showInfoTipos)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoTipos && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Clasificación de la campaña
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="tipo_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Tipo de Campaña *</FormLabel>
                      <FormMessage />
                    </div>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filtersData?.tipos_campania.map((tipo) => (
                          <SelectItem key={tipo.id} value={tipo.id.toString()}>
                            {tipo.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="formato_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Formato *</FormLabel>
                      <FormMessage />
                    </div>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona formato" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filtersData?.formatos_campania.map((formato) => (
                          <SelectItem key={formato.id} value={formato.id.toString()}>
                            {formato.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tipo_envio_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Tipo de Envío *</FormLabel>
                      <FormMessage />
                    </div>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona tipo de envío" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filtersData?.tipos_envio.map((tipo) => (
                          <SelectItem key={tipo.id} value={tipo.id.toString()}>
                            {tipo.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Estados */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle>Estados</CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoEstados(!showInfoEstados)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoEstados && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Estado actual de la campaña
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="estado_contenido_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Estado de Contenido *</FormLabel>
                      <FormMessage />
                    </div>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filtersData?.estados_contenido.map((estado) => (
                          <SelectItem key={estado.id} value={estado.id.toString()}>
                            {estado.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="estado_envio_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Estado de Envío *</FormLabel>
                      <FormMessage />
                    </div>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filtersData?.estados_envio.map((estado) => (
                          <SelectItem key={estado.id} value={estado.id.toString()}>
                            {estado.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="estado_reporte_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado de Reporte</FormLabel>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filtersData?.estados_reporte.map((estado) => (
                          <SelectItem key={estado.id} value={estado.id.toString()}>
                            {estado.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {requiereAprobacion && (
              <FormField
                control={form.control}
                name="estado_aprobacion_fk_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado de Aprobación</FormLabel>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona estado de aprobación" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filtersData?.estados_aprobacion.map((estado) => (
                          <SelectItem key={estado.id} value={estado.id.toString()}>
                            {estado.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </CardContent>
        </Card>

        {/* Contenido del Email */}
        {showEmail && (
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-muted-foreground" />
                Contenido del Email
              </CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoContenido(!showInfoContenido)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoContenido && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Detalles del contenido a enviar
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="asunto"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Asunto</FormLabel>
                  <FormControl>
                    <Input placeholder="Asunto del correo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="pre_encabezado"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pre-encabezado</FormLabel>
                    <FormControl>
                      <Input placeholder="Texto pre-encabezado" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="titulo_envio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Título del Envío</FormLabel>
                    <FormControl>
                      <Input placeholder="Título interno" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="contenido_html"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contenido HTML</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Contenido HTML del correo"
                      className="min-h-[150px] font-mono text-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="archivo_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL de Archivo</FormLabel>
                  <FormControl>
                    <Input type="url" placeholder="https://ejemplo.com/archivo.html" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
        )}

        {/* Contenido del WhatsApp */}
        {showWhatsApp && (
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
                Contenido del WhatsApp
              </CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoWA(!showInfoWA)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoWA && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Imagen, texto y botones del mensaje WhatsApp
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              {/* Formulario - 30% */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Imagen del Mensaje
                  </label>
                  <div className="mt-2">
                    <ImageUpload
                      value={waImageUrl || undefined}
                      onChange={handleWaImageChange}
                      disabled={waIsUploading}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Mensaje de WhatsApp
                  </label>
                  <Textarea
                    value={waMensaje}
                    onChange={(e) => setWaMensaje(e.target.value)}
                    rows={4}
                    placeholder="Texto del mensaje de WhatsApp. Usa *texto* para negritas y {nombre} para el nombre del destinatario."
                    className="mt-2"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Footer del Mensaje
                  </label>
                  <Input
                    value={waFooter}
                    onChange={(e) => setWaFooter(e.target.value.slice(0, 60))}
                    placeholder="Ej: Tonal Strategies"
                    className="mt-2"
                    maxLength={60}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{waFooter.length}/60</p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Botones CTA</label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setWaBotones([...waBotones, { texto: '', url: '' }])}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar Botón
                    </Button>
                  </div>
                  {waBotones.map((boton, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <Input
                        value={boton.texto}
                        onChange={(e) => {
                          const updated = [...waBotones]
                          updated[index] = { ...updated[index], texto: e.target.value }
                          setWaBotones(updated)
                        }}
                        placeholder="Texto del botón"
                        className="flex-1"
                      />
                      <Input
                        type="url"
                        value={boton.url}
                        onChange={(e) => {
                          const updated = [...waBotones]
                          updated[index] = { ...updated[index], url: e.target.value }
                          setWaBotones(updated)
                        }}
                        placeholder="https://..."
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setWaBotones(waBotones.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  {waBotones.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Sin botones CTA
                    </p>
                  )}
                </div>
              </div>

              {/* Preview WhatsApp - 70% */}
              <div className="flex items-center justify-center rounded-lg min-h-[300px]" style={{ backgroundColor: '#efeae2' }}>
                {waImageUrl || waMensaje.trim() || waFooter.trim() || waBotones.length > 0 ? (
                  <div className="flex items-center gap-1.5 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm" style={{ maxWidth: '320px' }}>
                      {waImageUrl && (
                        <div className="px-1.5 pt-1.5">
                          <img
                            src={waImageUrl}
                            alt="Imagen del mensaje"
                            className="w-full h-auto max-h-[300px] object-cover rounded-lg"
                          />
                        </div>
                      )}

                      {(waMensaje.trim() || waFooter.trim()) && (
                        <div className="px-3 py-2">
                          {waMensaje.trim() && (
                            <p
                              className="text-[13px] whitespace-pre-wrap text-gray-900 dark:text-gray-100 leading-snug"
                              dangerouslySetInnerHTML={{
                                __html: waMensaje
                                  .replace(/&/g, '&amp;')
                                  .replace(/</g, '&lt;')
                                  .replace(/>/g, '&gt;')
                                  .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
                              }}
                            />
                          )}
                          {waFooter.trim() && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{waFooter}</p>
                          )}
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span className="text-[10px] text-gray-500">10:30 a.m.</span>
                            <CheckCheck className="h-3.5 w-3.5 text-blue-500" />
                          </div>
                        </div>
                      )}

                      {waBotones.length > 0 && waBotones.some(b => b.texto.trim()) && (
                        <div className="border-t border-gray-200 dark:border-gray-700">
                          {waBotones.filter(b => b.texto.trim()).map((boton, index, arr) => (
                            <a
                              key={index}
                              href={boton.url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => !boton.url && e.preventDefault()}
                              className={`flex items-center justify-center gap-2 px-3 py-2.5 text-[13px] font-medium text-[#00a884] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                                index < arr.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''
                              }`}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              {boton.texto}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Emoji icon */}
                    <div className="w-7 h-7 rounded-full bg-white dark:bg-gray-700 shadow-sm flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">La vista previa del mensaje aparecerá aquí</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Programación */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle>Programación</CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoProgramacion(!showInfoProgramacion)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoProgramacion && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Fecha y hora de envío
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fecha_publicacion"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Fecha de Publicación *</FormLabel>
                      <FormMessage />
                    </div>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hora"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Hora de Envío *</FormLabel>
                      <FormMessage />
                    </div>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Adicionales */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle>Configuración Adicional</CardTitle>
              <button
                type="button"
                onClick={() => setShowInfoAdicional(!showInfoAdicional)}
                className="text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfoAdicional && (
                <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
                  Opciones y notas adicionales
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="requiere_aprobacion"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Requiere Aprobación</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Activar si la campaña requiere aprobación antes de envío
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="data-[state=checked]:bg-teal-500"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reporte"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reporte</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Reporte de la campaña..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Notas adicionales sobre la campaña"
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Botones de Acción */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(isEdit && initialData ? `/multichannel/campania/${initialData.id}` : '/multichannel/seguimiento')}
            disabled={isEdit ? updateCampania.isPending : createCampania.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isEdit ? updateCampania.isPending : createCampania.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            {(isEdit ? updateCampania.isPending : createCampania.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Guardar Cambios' : 'Crear Campaña'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
